import { Signal, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { AuthService } from '@modules/auth/services/auth.service';
import { UserRole } from '@modules/auth/models/enums/user-role.enum';
import { PermissionsService } from '@shared/services/permissions.service';

/** Kod funkcji systemowej wymaganej do moderacji tablicy „Szukam noclegu” (1:1 z backendem). */
export const REQUEST_BOARD_MODERATE_PERMISSION = 'request_board.moderate';

/**
 * Czy użytkownik o danej roli / uprawnieniach może moderować tablicę.
 * SUPER_ADMIN zawsze (backend omija guardy), pozostali — tylko z `request_board.moderate`.
 * Frontend wyłącznie chowa UI; egzekwuje backend (`/requests/admin/*` → 403).
 */
export function canModerateRequestBoard(
  role: string | null | undefined,
  hasSystemFunction: (code: string) => boolean,
): boolean {
  if (role === UserRole.SUPER_ADMIN) {
    return true;
  }
  return hasSystemFunction(REQUEST_BOARD_MODERATE_PERMISSION);
}

/**
 * `canModerateRequestBoard` jako sygnał dla widoków spoza modułu (np. linki do tablicy w
 * generycznych „Zgłoszeniach”). Przelicza się przy zmianie użytkownika (login / logout) i jego
 * uprawnień — m.in. po odświeżeniu z `GET /auth/permissions` na starcie aplikacji, które zwykle
 * kończy się już po utworzeniu widoku otwartego bezpośrednio z linku.
 * Wywoływać w kontekście wstrzykiwania (inicjalizator pola / konstruktor komponentu).
 */
export function injectCanModerateRequestBoard(): Signal<boolean> {
  const currentUser = toSignal(inject(AuthService).currentUser$, { initialValue: null });
  const permissions = toSignal(inject(PermissionsService).userPermissions$, {
    initialValue: new Set<string>(),
  });
  return computed(() => {
    const granted = permissions();
    return canModerateRequestBoard(currentUser()?.role ?? null, code => granted.has(code));
  });
}

/**
 * Guard tras `/request-board/**` — bez uprawnienia przekierowanie na dashboard.
 * Zakłada, że `AuthGuard` już sprawdził zalogowanie.
 *
 * Uprawnienia zapisane przy logowaniu mogą być nieaktualne (np. `request_board.moderate` nadane
 * backfillem po wdrożeniu) — przy braku uprawnienia guard najpierw odświeża je z
 * `GET /auth/permissions` i dopiero wtedy decyduje (bez wymuszania ponownego logowania).
 */
export const requestBoardModerateGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const permissionsService = inject(PermissionsService);
  const router = inject(Router);

  const isAllowed = (): boolean =>
    canModerateRequestBoard(authService.currentUser$.value?.role ?? null, code =>
      permissionsService.hasSystemFunction(code),
    );

  if (isAllowed()) {
    return true;
  }
  const denied = router.createUrlTree(['/dashboard']);
  return authService.refreshPermissions().pipe(
    map(() => (isAllowed() ? true : denied)),
    catchError(() => of(denied)),
  );
};
